import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Descriptions, Empty, Form, Input, Modal, Radio, Row, Space, Switch, Table, Tag, Tooltip, Typography, message } from "antd";
import { CheckOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import ScheduleTag from "../../shared/ScheduleTag";
import { useScheduleTags } from "../../shared/useScheduleTags";

const COLOR_SWATCHES = [
    "#FF4D4F",
    "#FA8C16",
    "#FAAD14",
    "#52C41A",
    "#13C2C2",
    "#1677FF",
    "#2F54EB",
    "#722ED1",
    "#EB2F96",
    "#8C8C8C",
];

function getErrorMessage(error) {
    return error?.response?.data?.detail || error?.message || "保存标签失败";
}

const ScheduleTagManageModal = ({ open, onClose }) => {
    const [form] = Form.useForm();
    const [selectedTagId, setSelectedTagId] = useState(null);
    const [mode, setMode] = useState(null);
    const { definitions, definitionMap, isLoading, isFetching, loadError, refetch, createTag, updateTag, isSaving } = useScheduleTags({
        includeInactive: true,
        enabled: open,
    });
    const selectedTag = useMemo(
        () => definitions.find((definition) => definition.id === selectedTagId) || null,
        [definitions, selectedTagId],
    );
    const selectedColor = Form.useWatch("color_code", form) || "#1677FF";
    const previewName = Form.useWatch("tag_name", form) || selectedTag?.tag_name || "TAG";

    const beginCreate = () => {
        setMode("create");
        setSelectedTagId(null);
        form.setFieldsValue({
            tag_name: "",
            color_code: "#1677FF",
            description: "",
            is_active: true,
        });
    };

    const clearEditor = () => {
        setMode(null);
        setSelectedTagId(null);
        form.resetFields();
    };

    const beginEdit = (definition) => {
        setMode("edit");
        setSelectedTagId(definition.id);
        form.setFieldsValue({
            tag_name: definition.tag_name,
            color_code: definition.color_code,
            description: definition.description || "",
            is_active: definition.is_active,
        });
    };

    useEffect(() => {
        if (open) {
            clearEditor();
        }
        // The form instance is stable; reset only when the modal opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleSave = async (values) => {
        try {
            if (mode === "create") {
                const response = await createTag({
                    tag_name: values.tag_name,
                    color_code: values.color_code,
                    description: values.description || null,
                });
                message.success("标签已创建");
                const createdId = response?.data?.id;
                await refetch();
                if (createdId) {
                    setSelectedTagId(createdId);
                    setMode("edit");
                }
            } else if (selectedTag) {
                await updateTag({
                    tagId: selectedTag.id,
                    data: {
                        color_code: values.color_code,
                        description: values.description || null,
                        is_active: values.is_active,
                        expected_updated_at: selectedTag.updated_at,
                    },
                });
                message.success("标签已更新");
                await refetch();
            }
        } catch (error) {
            message.error(getErrorMessage(error));
        }
    };

    const columns = [
        {
            title: "标签",
            dataIndex: "tag_name",
            key: "tag_name",
            render: (tagName) => <ScheduleTag name={tagName} definitionMap={definitionMap} />,
        },
        {
            title: "状态",
            dataIndex: "is_active",
            key: "is_active",
            width: 76,
            render: (isActive) => <Tag color={isActive ? "success" : "default"}>{isActive ? "启用" : "停用"}</Tag>,
        },
        {
            title: "",
            key: "action",
            width: 48,
            render: (_, record) => (
                <Tooltip title="编辑标签">
                    <Button type="text" icon={<EditOutlined />} onClick={() => beginEdit(record)} aria-label={`编辑 ${record.tag_name}`} />
                </Tooltip>
            ),
        },
    ];

    return (
        <Modal
            title="航班计划标签管理"
            open={open}
            onCancel={onClose}
            width={960}
            footer={
                <Button onClick={onClose} disabled={isSaving} aria-label="完成">
                    完成
                </Button>
            }
        >
            <Alert
                type="warning"
                showIcon
                message="标签颜色和状态是全局配置，修改后会影响所有旧版航班计划。"
                style={{ marginBottom: 16 }}
            />
            {loadError ? (
                <Alert
                    type="error"
                    showIcon
                    message="标签配置加载失败"
                    action={<Button type="text" icon={<ReloadOutlined />} onClick={() => refetch()} aria-label="重新加载" />}
                    style={{ marginBottom: 16 }}
                />
            ) : null}

            <Row gutter={24}>
                <Col span={11}>
                    <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 12 }}>
                        <Typography.Title level={5} style={{ margin: 0 }}>
                            标签定义
                        </Typography.Title>
                        <Button icon={<PlusOutlined />} onClick={beginCreate} aria-label="新建标签">
                            新建标签
                        </Button>
                    </Space>
                    <Table
                        rowKey="id"
                        size="small"
                        columns={columns}
                        dataSource={definitions}
                        loading={isLoading || isFetching}
                        pagination={false}
                        scroll={{ y: 430 }}
                        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无标签" /> }}
                        onRow={(record) => ({ onClick: () => beginEdit(record) })}
                        rowClassName={(record) => (record.id === selectedTagId ? "ant-table-row-selected" : "")}
                    />
                </Col>

                <Col span={13}>
                    {mode ? (
                        <>
                            <Typography.Title level={5}>{mode === "create" ? "新建标签" : `编辑 ${selectedTag?.tag_name || ""}`}</Typography.Title>
                            <Form form={form} layout="vertical" requiredMark={false} onFinish={handleSave}>
                                <Form.Item
                                    name="tag_name"
                                    label="标签名称"
                                    rules={[
                                        { required: true, whitespace: true, message: "请输入标签名称" },
                                        { max: 50, message: "标签名称不能超过 50 个字符" },
                                        { pattern: /^[^,\r\n]+$/, message: "标签名称不能包含逗号或换行" },
                                    ]}
                                >
                                    <Input disabled={mode === "edit"} maxLength={50} />
                                </Form.Item>

                                <Form.Item name="color_code" label="标签颜色" rules={[{ required: true }]}>
                                    <Radio.Group style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                                        {COLOR_SWATCHES.map((color) => (
                                            <Tooltip title={color} key={color}>
                                                <Radio.Button
                                                    value={color}
                                                    aria-label={`选择颜色 ${color}`}
                                                    style={{
                                                        width: 30,
                                                        height: 30,
                                                        padding: 0,
                                                        borderRadius: 4,
                                                        border: selectedColor === color ? "2px solid #1F1F1F" : "1px solid #D9D9D9",
                                                        background: color,
                                                        color: "#FFFFFF",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {selectedColor === color ? <CheckOutlined /> : null}
                                                </Radio.Button>
                                            </Tooltip>
                                        ))}
                                    </Radio.Group>
                                </Form.Item>

                                <Form.Item label="实时预览">
                                    <ScheduleTag
                                        name={String(previewName).trim() || "TAG"}
                                        definitionMap={new Map([[String(previewName).trim().toLocaleLowerCase(), { color_code: selectedColor, is_active: true }]])}
                                    />
                                </Form.Item>

                                <Form.Item name="description" label="说明" rules={[{ max: 500, message: "说明不能超过 500 个字符" }]}>
                                    <Input.TextArea rows={4} maxLength={500} showCount />
                                </Form.Item>

                                {mode === "edit" ? (
                                    <Form.Item name="is_active" label="启用状态" valuePropName="checked">
                                        <Switch disabled={selectedTag?.is_system_tag} />
                                    </Form.Item>
                                ) : null}

                                <Form.Item style={{ marginBottom: selectedTag ? 16 : 0 }}>
                                    <Space>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={isSaving}
                                            icon={mode === "create" ? <PlusOutlined /> : <SaveOutlined />}
                                            aria-label={mode === "create" ? "创建标签" : "保存修改"}
                                        >
                                            {mode === "create" ? "创建标签" : "保存修改"}
                                        </Button>
                                        {mode === "edit" ? <Button onClick={clearEditor}>取消编辑</Button> : null}
                                    </Space>
                                </Form.Item>
                            </Form>

                            {selectedTag ? (
                                <Descriptions size="small" column={1} bordered>
                                    <Descriptions.Item label="类型">{selectedTag.is_system_tag ? "系统标签" : "普通标签"}</Descriptions.Item>
                                    <Descriptions.Item label="创建">
                                        {selectedTag.created_by || "-"} · {dayjs(selectedTag.created_at).format("YYYY-MM-DD HH:mm:ss")}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="最近修改">
                                        {selectedTag.updated_by || "-"} · {dayjs(selectedTag.updated_at).format("YYYY-MM-DD HH:mm:ss")}
                                    </Descriptions.Item>
                                </Descriptions>
                            ) : null}
                        </>
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="请选择左侧标签进行编辑，或点击新建标签。"
                            style={{ marginTop: 120 }}
                        />
                    )}
                </Col>
            </Row>
        </Modal>
    );
};

export default ScheduleTagManageModal;
